import { isEqual } from './isEqual';
/**
 * Two values are considered "equal" if they are referentially equal.
 */
export function checkValuesReferentiallyEqual(opt1, opt2) {
    return opt1 === opt2;
}
/**
 * Two sets of objects are considered "equal" when their shapes are deeply equal, regardless of
 * any referential equality.
 */
export function checkObjectsDeepEqual(opt1, opt2) {
    if (!opt1 || !opt2) {
        return opt1 === opt2;
    }
    if (Array.isArray(opt1) && Array.isArray(opt2)) {
        return isEqual(opt1, opt2);
    }
    if (!Array.isArray(opt1) && !Array.isArray(opt2)) {
        return isEqual([opt1], [opt2]);
    }
    return opt1 === opt2;
}
/**
 * Two objects are considered "equal" if all of the keys' values for the object are referentially
 * equal to the keys' values for the other object.
 */
function isEqualPerObjectKey(opt1, opt2) {
    if (!opt1 || !opt2) {
        return opt1 === opt2;
    }
    var opt1Keys = Object.keys(opt1);
    var opt2Keys = Object.keys(opt2);
    // If the objects have a different amount of keys, or if the keys themselves are different,
    // then we definitely know the objects are  different from one another:
    if (opt1Keys.length !== opt2Keys.length || !isEqual(opt1Keys, opt2Keys)) {
        return false;
    }
    // If they keys are the same, then referentially compare all key values between the objects
    // to determine whether the objects are equal. We explicitly want to ensure any key values
    // that are objects or functions are considered different if their reference is different,
    // even if the underlying contents would be considered the same by a deep isEqual check.
    // This is critical to ensure that your React components re-compute everything if any reference within
    // an object changes between render cycles, otherwise the consumer would have stale
    // references.
    return opt1Keys.every(function (k) {
        return opt1[k] === opt2[k];
    });
}
/**
 * Two sets of objects are considered "equal" if, for every object, all of the keys'
 * values for the object are referentially equal to the keys' values for the object
 * in the same position of the other set.
 */
export function checkObjectsReferentiallyEqualPerKey(opt1, opt2) {
    // If both objects are arrays, do more checking to see if they are considered equal:
    if (Array.isArray(opt1) && Array.isArray(opt2)) {
        // If the objects lengths differ, we know the objects are different:
        if (opt1.length !== opt2.length) {
            return false;
        }
        // If the objects lengths are the same, we need to check that each object within each
        // list are considered equal, in the same order as the other list:
        return opt1.every(function (o1, index) { return isEqualPerObjectKey(o1, opt2[index]); });
    }
    // If both objects are not arrays, do more checking to see if they are considered equal:
    if (!Array.isArray(opt1) && !Array.isArray(opt2)) {
        return isEqualPerObjectKey(opt1, opt2);
    }
    // If one object is an array, and the other is not, we know they are different:
    return false;
}
/**
 * Two sets of objects are considered "equal" if, for every object, all of the keys passed
 * in match referentially to the corresponding object in the other set
 */
export function checkObjectsReferentiallyEqualForKeys(keys) {
    return function (opt1, opt2) {
        if (!opt1 || !opt2) {
            return opt1 === opt2;
        }
        if (Array.isArray(opt1) && Array.isArray(opt2)) {
            return opt1.every(function (o1, index) { return checkObjectsReferentiallyEqualForKeys(keys)(o1, opt2[index]); });
        }
        return keys.every(function (k) {
            return opt1[k] === opt2[k];
        });
    };
}
//# sourceMappingURL=equalityChecks.js.map